import React, { useContext, useEffect, useState } from "react";
import { Check, Eye, EyeOff, LockKeyhole, MailIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../context/auth.context";
import { Employee } from "../../../services";
import styles from "./ConfirmEmployee.module.scss";

const MIN_SUCCESS_LOADING_MS = 1200;

const ConfirmEmployeePage = () => {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errorTitle, setErrorTitle] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    setLoader(true);
    Employee.getEmailByEmployee(code)
      .then((response) => {
        if (response.status === 200) {
          setEmail(response.data);
        } else {
          setEmailError(true);
        }
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
        setEmailError(true);
      });
  }, [code]);

  const waitBeforeRedirect = () => new Promise((resolve) => setTimeout(resolve, MIN_SUCCESS_LOADING_MS));

  const setPasswordEmployee = () => {
    if (!password || !confirmPassword) {
      setErrorTitle(t("confirmEmployee.validation.fillAllFields"));
      return;
    }

    if (password !== confirmPassword) {
      setErrorTitle(t("confirmEmployee.validation.passwordsMismatch"));
      return;
    }

    setErrorTitle();
    setIsLoading(true);

    const data = {
      email,
      password,
      confirmPassword,
      code
    };

    Employee.setEmployeePassword( data)
      .then((response) => {
        if (response.status === 200 && response.data?.accessToken) {
          const userData = {
            ...response.data,
            isAuthenticated: true,
          };

          setIsSuccess(true);
          setIsLoading(false);

          return waitBeforeRedirect().then(() => {
            auth.login(userData.accessToken);
            userData.userType === "expeditor" ? navigate("/orders/available") : navigate("/main");
          });
        }

        throw new Error(t("confirmEmployee.toast.genericError"));
      })
      .catch(() => {
        setIsSuccess(false);
        setErrorTitle(t("confirmEmployee.toast.genericError"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setPasswordEmployee();
    }
  };

  const handleLogoClick = () => {
    navigate("/login");
  };

  const renderLoading = () => (
    <div className={styles.loaderState}>
      <div className="loader brand" />
      <div className={styles.loaderText}>{t("confirmEmployee.loading")}</div>
    </div>
  );

  const renderExpiredLink = () => (
    <div className={styles.errorState}>
      <div className={styles.errorText}>{t("confirmEmployee.error.expiredLink")}</div>
      <Link to="/login" className={styles.loginLink}>
        {t("confirmEmployee.error.loginButton")}
      </Link>
    </div>
  );

  const renderSuccess = () => (
    <div className={styles.successState}>
      <div className={styles.successIconWrap}>
        <div className={styles.successIconRing}>
          <Check size={34} strokeWidth={3.2} />
        </div>
      </div>
      <h1 className={styles.successTitle}>{t("confirmEmployee.success.progressTitle")}</h1>
      <p className={styles.successText}>{t("confirmEmployee.success.progressMessage")}</p>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} />
      </div>
      <div className={styles.progressCaption}>{t("confirmEmployee.success.progressCaption")}</div>
    </div>
  );

  const renderPasswordField = ({
    id,
    label,
    placeholder,
    value,
    onChange,
    isVisible,
    onToggleVisibility,
  }) => (
    <label className={styles.passwordField} htmlFor={id}>
      <span className={styles.label}>{label}</span>
      <span className={styles.inputWrap}>
        <input
          id={id}
          type={isVisible ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        {value && (
          <button type="button" onClick={onToggleVisibility} aria-label={isVisible ? "Hide password" : "Show password"}>
            {isVisible ? <EyeOff size={22} strokeWidth={2} /> : <Eye size={22} strokeWidth={2} />}
          </button>
        )}
      </span>
    </label>
  );

  const renderForm = () => (
    <>
      <div className={styles.iconWrap}>
        <LockKeyhole size={28} strokeWidth={2.2} />
      </div>
      <h1 className={styles.title}>{t("confirmEmployee.pageTitle")}</h1>
      <p className={styles.subtitle}>{t("confirmEmployee.form.prompt")}</p>

      <div className={styles.form}>
        <div className={styles.field}>
          <div className={styles.label}>{t("confirmEmployee.form.accountLabel")}</div>
          <div className={styles.emailBox}>
            <span className={styles.emailIcon}>
              <MailIcon size={22} strokeWidth={2} />
            </span>
            <span className={styles.emailText}>{email}</span>
          </div>
        </div>

        {renderPasswordField({
          id: "employee-password",
          label: t("confirmEmployee.form.passwordLabel"),
          placeholder: t("confirmEmployee.form.passwordPlaceholder"),
          value: password,
          onChange: setPassword,
          isVisible: isPasswordVisible,
          onToggleVisibility: () => setIsPasswordVisible((value) => !value),
        })}

        {renderPasswordField({
          id: "employee-confirm-password",
          label: t("confirmEmployee.form.confirmPasswordLabel"),
          placeholder: t("confirmEmployee.form.confirmPasswordPlaceholder"),
          value: confirmPassword,
          onChange: setConfirmPassword,
          isVisible: isConfirmPasswordVisible,
          onToggleVisibility: () => setIsConfirmPasswordVisible((value) => !value),
        })}

        {errorTitle && <div className={styles.formError}>{errorTitle}</div>}

        <button className={styles.submitButton} type="button" onClick={setPasswordEmployee} disabled={isLoading}>
          {isLoading ? <span className={styles.buttonLoader} /> : t("confirmEmployee.form.saveButton")}
        </button>
      </div>
    </>
  );

  const renderContent = () => {
    if (loader) return renderLoading();
    if (emailError) return renderExpiredLink();
    if (isSuccess) return renderSuccess();
    return renderForm();
  };

  return (
    <main className={styles.page}>
      <button type="button" className={styles.logo} onClick={handleLogoClick} aria-label="Go to login">
        <img src="/icons/ic_biny_dark.svg" alt="biny" />
      </button>
      <section className={styles.card}>{renderContent()}</section>
    </main>
  );
};

export default ConfirmEmployeePage;
